import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Bell, AlertCircle, Clock, FileText, Activity, ChevronRight, Check, RefreshCcw, CalendarClock } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { cn, dateLocale } from "@/lib/utils";
import { useNotifications } from "@/hooks/useNotifications";
import { useLang, translateFr as tfr } from "@/i18n";
import api from "@/lib/api";
import { offerAlertTitle } from "@/components/contracts/negotiationEvents";

const FILTERS = [
  { v: "all", label: "Tout" },
  { v: "urgent", label: "Urgent" },
  { v: "today", label: tfr("Aujourd'hui") },
  { v: "docs", label: "Documents" },
];

export default function NotificationsBell() {
  const { data, refresh } = useNotifications();
  const { tf: tfOffer } = useLang();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");

  const total = data.total || 0;
  const unread = data.unread ?? total;
  const counts = data.counts || {};
  const items = data.items || {};

  const onOpenChange = (o) => {
    setOpen(o);
    if (o && unread > 0) {
      // mark current items as seen → badge reflects UNREAD notifications only
      api.post("/notifications/ack", { keys: data.keys || [] }).then(() => refresh()).catch(() => {});
    }
  };

  const showOverdue = filter === "all" || filter === "urgent";
  const showToday = filter === "all" || filter === "today";
  const showExpired = filter === "all" || filter === "urgent" || filter === "docs";
  const showSoon = filter === "all" || filter === "docs";
  const showRenewCritical = filter === "all" || filter === "urgent";
  const showRenewSoon = filter === "all";

  const isEmpty = total === 0;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="relative inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors"
          data-testid="notifications-bell"
          aria-label={`Notifications (${unread} non lues)`}
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className={cn(
                "absolute top-1 right-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-bold tabular-nums leading-none border-2 border-background",
                (counts.relances_overdue || 0) + (counts.docs_expired || 0) + (counts.renewals_critical || 0) > 0
                  ? "bg-danger text-white"
                  : "bg-warning text-white"
              )}
              data-testid="notifications-badge"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[380px] p-0 bg-popover text-popover-foreground border-border shadow-2xl max-h-[80vh] overflow-hidden flex flex-col"
        data-testid="notifications-panel"
      >
        {/* Header */}
        <header className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold tracking-tight">Notifications</h3>
            {total > 0 && (
              <Badge className="bg-danger/15 text-danger border border-danger/30 text-[10px]">
                {total} {total > 1 ? "urgentes" : "urgente"}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide -mx-1 px-1">
            {FILTERS.map((f) => (
              <button
                key={f.v}
                onClick={() => setFilter(f.v)}
                className={cn(
                  "shrink-0 h-7 px-3 rounded-full text-xs font-medium transition-colors border",
                  filter === f.v
                    ? "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/35"
                    : "bg-surface text-muted-foreground border-border hover:text-foreground hover:bg-surface-hover"
                )}
                data-testid={`notif-filter-${f.v}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {isEmpty ? (
            <div className="px-6 py-12 text-center" data-testid="notif-empty">
              <div className="h-12 w-12 mx-auto rounded-2xl bg-success/15 border border-success/30 text-success flex items-center justify-center mb-3">
                <Check className="h-5 w-5" />
              </div>
              <div className="text-sm font-semibold">{tfr("Tout est à jour")}</div>
              <div className="text-xs text-muted-foreground mt-1">{tfr("Aucune action urgente.")}</div>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {/* RELANCES URGENT */}
              {showOverdue && items.relances_overdue?.length > 0 && (
                <Section
                  title={tfr("Relances en retard")}
                  count={counts.relances_overdue}
                  Icon={AlertCircle}
                  tone="danger"
                  testid="section-overdue"
                >
                  {items.relances_overdue.map((p) => (
                    <NotifRow
                      key={p.id}
                      to={`/players/${p.id}`}
                      onClick={() => setOpen(false)}
                      title={p.name}
                      subtitle={[p.club, p.days != null ? `${p.days} ${tfr("jours sans relance")}` : tfr("Jamais relancé")].filter(Boolean).join(" · ")}
                      tone="danger"
                      testid={`notif-overdue-${p.id}`}
                    />
                  ))}
                  <ViewAllLink to="/relances" onClick={() => setOpen(false)} count={counts.relances_overdue} />
                </Section>
              )}

              {/* RELANCES TODAY */}
              {showToday && items.relances_today?.length > 0 && (
                <Section
                  title={tfr("À faire aujourd'hui")}
                  count={counts.relances_today}
                  Icon={Clock}
                  tone="warning"
                  testid="section-today"
                >
                  {items.relances_today.map((it) => (
                    <NotifRow
                      key={`${it.kind}-${it.id}`}
                      to={it.player_id ? `/players/${it.player_id}` : "/calendar"}
                      onClick={() => setOpen(false)}
                      title={it.title}
                      subtitle={new Date(it.at).toLocaleTimeString(dateLocale(), { hour: "2-digit", minute: "2-digit" })}
                      tone="warning"
                      testid={`notif-today-${it.id}`}
                    />
                  ))}
                </Section>
              )}

              {/* OFFER DEADLINE ALERTS (Session 4) */}
              {["all", "urgent"].includes(filter) && items.offer_alerts?.length > 0 && (
                <Section
                  title={tfOffer("Offres — échéances")}
                  count={counts.offer_alerts}
                  Icon={CalendarClock}
                  tone="danger"
                  testid="section-offer-alerts"
                >
                  {items.offer_alerts.map((a) => (
                    <NotifRow
                      key={a.id}
                      to={a.link || `/contracts/${a.contract_id}?tab=negotiation`}
                      onClick={() => { api.patch(`/negotiations/alerts/${a.id}/read`).catch(() => {}); refresh?.(); setOpen(false); }}
                      title={a.alert_type === "SIGNATURE" ? a.title : offerAlertTitle(a, tfOffer)}
                      subtitle={a.alert_type === "SIGNATURE" ? (a.subtitle || "") : [`V${a.version_number}`, a.counterparty, a.valid_until ? new Date(a.valid_until).toLocaleString(dateLocale(), { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : null].filter(Boolean).join(" · ")}
                      tone={a.alert_type === "SIGNATURE" ? (a.tone === "success" ? "success" : a.tone === "danger" ? "danger" : "warning") : ["EXPIRED", "D1"].includes(a.alert_type) ? "danger" : "warning"}
                      testid={`notif-offer-${a.id}`}
                    />
                  ))}
                </Section>
              )}

              {/* RENEWALS CRITICAL */}
              {showRenewCritical && items.renewals_critical?.length > 0 && (
                <Section
                  title={tfr("Renouvellements critiques")}
                  count={counts.renewals_critical}
                  Icon={RefreshCcw}
                  tone="danger"
                  testid="section-renewals-critical"
                >
                  {items.renewals_critical.map((r) => (
                    <NotifRow
                      key={r.id}
                      to={`/contracts/renewals/${r.id}`}
                      onClick={() => setOpen(false)}
                      title={r.player_name || r.contract_title}
                      subtitle={[r.contract_title, r.reason].filter(Boolean).join(" · ")}
                      tone="danger"
                      testid={`notif-renewal-critical-${r.id}`}
                    />
                  ))}
                </Section>
              )}

              {/* PLACEMENTS ATTENTION */}
              {items.placements_attention?.length > 0 && (
                <Section
                  title={tfr("Placements à suivre")}
                  count={counts.placements_attention}
                  Icon={AlertCircle}
                  tone="warning"
                  testid="section-placements"
                >
                  {items.placements_attention.map((p) => (
                    <NotifRow
                      key={p.id}
                      to={`/placements/${p.id}`}
                      onClick={() => setOpen(false)}
                      title={p.player_name}
                      subtitle={p.reason}
                      tone={p.state === "ACTION_REQUIRED" ? "danger" : "warning"}
                      testid={`notif-placement-${p.id}`}
                    />
                  ))}
                </Section>
              )}

              {/* RENEWALS EXPIRING */}
              {showRenewSoon && items.renewals_expiring?.length > 0 && (
                <Section
                  title={tfr("Renouvellements à préparer")}
                  count={counts.renewals_expiring}
                  Icon={RefreshCcw}
                  tone="warning"
                  testid="section-renewals-expiring"
                >
                  {items.renewals_expiring.map((r) => (
                    <NotifRow
                      key={r.id}
                      to={`/contracts/renewals/${r.id}`}
                      onClick={() => setOpen(false)}
                      title={r.player_name || r.contract_title}
                      subtitle={[r.contract_title, r.days_left != null ? `${tfr("Expire dans")} ${r.days_left} ${tfr("jours")}` : null].filter(Boolean).join(" · ")}
                      tone="warning"
                      testid={`notif-renewal-expiring-${r.id}`}
                    />
                  ))}
                </Section>
              )}

              {/* DOCS EXPIRED */}
              {showExpired && items.docs_expired?.length > 0 && (
                <Section
                  title={tfr("Documents expirés")}
                  count={counts.docs_expired}
                  Icon={FileText}
                  tone="danger"
                  testid="section-expired"
                >
                  {items.docs_expired.map((d) => (
                    <NotifRow
                      key={d.id}
                      to={d.player_id ? `/players/${d.player_id}` : "#"}
                      onClick={() => setOpen(false)}
                      title={d.label}
                      subtitle={[d.player_name, `${tfr("Expiré il y a")} ${Math.abs(d.days_left)} ${tfr("jours")}`].filter(Boolean).join(" · ")}
                      tone="danger"
                      testid={`notif-expired-${d.id}`}
                    />
                  ))}
                </Section>
              )}

              {/* DOCS SOON */}
              {showSoon && items.docs_expiring_soon?.length > 0 && (
                <Section
                  title={tfr("Documents bientôt expirés")}
                  count={counts.docs_expiring_soon}
                  Icon={FileText}
                  tone="warning"
                  testid="section-soon"
                >
                  {items.docs_expiring_soon.map((d) => (
                    <NotifRow
                      key={d.id}
                      to={d.player_id ? `/players/${d.player_id}` : "#"}
                      onClick={() => setOpen(false)}
                      title={d.label}
                      subtitle={[d.player_name, `${tfr("Expire dans")} ${d.days_left} ${tfr("jours")}`].filter(Boolean).join(" · ")}
                      tone="warning"
                      testid={`notif-soon-${d.id}`}
                    />
                  ))}
                </Section>
              )}

              {/* No items match filter */}
              {!showOverdue && !showToday && !showExpired && !showSoon && (
                <div className="px-6 py-10 text-center text-xs text-muted-foreground">
                  {tfr("Aucun élément dans cette catégorie.")}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer activity hint */}
        {(counts.recent_activity || 0) > 0 && (
          <div className="px-4 py-2.5 border-t border-border text-[11px] text-muted-foreground flex items-center gap-1.5">
            <Activity className="h-3 w-3" />
            <span>{counts.recent_activity} interactions cette semaine</span>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

const TONE_CLASS = {
  danger: "text-danger bg-danger/10 border-danger/30",
  warning: "text-warning bg-warning/10 border-warning/30",
  info: "text-info bg-info/10 border-info/30",
  success: "text-success bg-success/10 border-success/30",
};

function Section({ title, count, Icon, tone, children, testid }) {
  return (
    <section className="py-2" data-testid={testid}>
      <div className="px-4 py-2 flex items-center gap-2">
        <Icon className={cn("h-3.5 w-3.5", `text-${tone}`)} />
        <h4 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-foreground/85">
          {title}
        </h4>
        <Badge variant="outline" className={cn("text-[10px] h-5 px-1.5 ml-auto", TONE_CLASS[tone])}>
          {count}
        </Badge>
      </div>
      <ul className="">{children}</ul>
    </section>
  );
}

function NotifRow({ to, onClick, title, subtitle, tone, testid }) {
  return (
    <li>
      <Link
        to={to}
        onClick={onClick}
        className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-hover transition-colors group"
        data-testid={testid}
      >
        <span className={cn("h-2 w-2 rounded-full shrink-0", `bg-${tone}`)} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{title}</div>
          {subtitle && <div className="text-[11px] text-muted-foreground truncate mt-0.5">{subtitle}</div>}
        </div>
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40 group-hover:text-foreground transition-colors" />
      </Link>
    </li>
  );
}

function ViewAllLink({ to, onClick, count }) {
  return (
    <li>
      <Link
        to={to}
        onClick={onClick}
        className="block px-4 py-2 text-[11px] font-medium text-violet-600 dark:text-violet-400 hover:underline transition-colors"
      >
        {tfr("Voir les")} {count} {tfr("relances en retard")} →
      </Link>
    </li>
  );
}
